import { isDefined } from "@clipboard-health/util-ts";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics/events";
import { logEvent } from "@src/appV2/lib/analytics/log";
import { convertToGeoLocation } from "@src/appV2/Location";
import { useShiftRouteEstimate } from "@src/appV2/redesign/ArrivalTime/ShiftRouteEstimate/useShiftRouteEstimate";
import {
  addMinutes,
  differenceInMinutes,
  isBefore,
  parseISO,
  secondsToMilliseconds,
  subHours,
} from "date-fns";

import type { ArrivalContext } from "../types";

interface UseTravelTimeParams {
  arrivalContext: ArrivalContext;
  facilityCoordinates?: [number, number];
  workerId?: string;
  shiftId?: string;
  workplaceId?: string;
  shiftStartIso?: string;
}

const FREQUENT_REFRESH_INTERVAL_THRESHOLD_MINUTES = 10;
const FREQUENT_REFRESH_INTERVAL_SECONDS = 10;
const DEFAULT_REFRESH_INTERVAL_SECONDS = 300;

/**
 * Single-responsibility hook for calculating travel time
 * Only calculates when we have a travel location and facility coordinates
 */
export function useTravelTime({
  arrivalContext,
  facilityCoordinates,
  workerId,
  shiftId,
  workplaceId,
  shiftStartIso,
}: UseTravelTimeParams) {
  const facilityGeoLocation = isDefined(facilityCoordinates)
    ? convertToGeoLocation(facilityCoordinates)
    : undefined;

  const travelFromGeoLocation = arrivalContext.travelFromLocation
    ? {
        latitude: arrivalContext.travelFromLocation.lat,
        longitude: arrivalContext.travelFromLocation.lng,
      }
    : undefined;
  const shouldCalculateDistance =
    isDefined(facilityGeoLocation) &&
    isDefined(travelFromGeoLocation) &&
    (arrivalContext.state === "traveling" || arrivalContext.state === "ready-to-travel");

  let departureTime = isDefined(shiftStartIso) ? subHours(parseISO(shiftStartIso), 1) : undefined;
  const minutesToShiftStart = isDefined(shiftStartIso)
    ? differenceInMinutes(parseISO(shiftStartIso), new Date())
    : 0;
  const refetchTravelTimeIntervalSeconds =
    minutesToShiftStart < FREQUENT_REFRESH_INTERVAL_THRESHOLD_MINUTES
      ? FREQUENT_REFRESH_INTERVAL_SECONDS
      : DEFAULT_REFRESH_INTERVAL_SECONDS;

  if (isDefined(departureTime) && isBefore(departureTime, new Date())) {
    departureTime = addMinutes(new Date(), 5);
  }

  const {
    data: routeEstimateData,
    isLoading,
    error,
  } = useShiftRouteEstimate(
    {
      source: travelFromGeoLocation,
      destination: facilityGeoLocation,
      provider: "AWS_LOCATION",
      departureTimeIso: departureTime?.toISOString(),
      shiftId,
      workerId: workerId ?? "",
      workplaceId: workplaceId ?? "",
      shiftStartIso,
      onSuccess: (data) => {
        logEvent(APP_V2_APP_EVENTS.ESTIMATED_TRAVEL_TIME_CALCULATED, {
          durationInMinutes: data?.durationInMinutes,
          workerId: workerId ?? "",
          shiftId: shiftId ?? "",
          workplaceId: workplaceId ?? "",
          shiftStartIso: shiftStartIso ?? "",
        });
      },
      onError: () => {
        logEvent(APP_V2_APP_EVENTS.ESTIMATED_TRAVEL_TIME_CALCULATION_FAILED, {
          error,
          workerId: workerId ?? "",
          shiftId: shiftId ?? "",
          workplaceId: workplaceId ?? "",
          shiftStartIso: shiftStartIso ?? "",
        });
      },
    },
    {
      enabled: shouldCalculateDistance && isDefined(workerId) && isDefined(workplaceId),
      staleTime: secondsToMilliseconds(refetchTravelTimeIntervalSeconds),
    }
  );

  return {
    estimatedTravelTime: routeEstimateData?.durationInMinutes,
    isLoading,
    travelFromType: arrivalContext.travelFromLocation?.type,
    error,
  };
}
